import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useI18n } from "i18n";
import classnames from "classnames";
import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { FormTextInput } from "@remo-co/ui-core/src/components/FormTextInput";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { IEvent } from "modules/event/types";
import {
  PROCEED_TO_EVENT_QUERY_PARAMETER,
  PROCEED_TO_EVENT_QUERY_PARAMETER_VALUE,
} from "modules/eventLanding/constants";
import sendSignInLinkToEmail from "modules/auth/utils/sendSignInLinkToEmail";
import { selectDirectTableLink } from "modules/conversation/hooks/useCopyTableLink/selectors";
import { selectIsEmailPasswordEnabled } from "modules/companySettings/redux/selectors";
import {
  selectPreferredLoginMethods,
  selectShowUserPreferredLoginBypass,
} from "modules/auth/redux/selectors";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { setMagicLinkEmail } from "modules/auth/redux/authSlice";
import { useAppDispatch } from "store/hooks";
import { CompanyPublicSettings } from "modules/company/types";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isCustomDomainEnabled } from "modules/settings/utils/isCustomDomainEnabled";
import { CHECK_EMAIL_TO_LOGIN_MODES } from "modules/auth/constants";
import { useStyles } from "./styles";
import { EmailInput } from "../../types";
import { emailSchema, getEmailErrorMessage } from "./validators";

interface Props {
  eventCompanySettings: CompanyPublicSettings | undefined;
  isHappeningNow: boolean;
  event: IEvent;
  email: EmailInput;
  setEmail: Dispatch<SetStateAction<EmailInput>>;
  setGoToLogin: Dispatch<SetStateAction<boolean>>;
  setShowUserDetailsForm: Dispatch<SetStateAction<boolean>>;
}

export const EmailOption = ({
  eventCompanySettings,
  isHappeningNow,
  email,
  setEmail,
  setGoToLogin,
  setShowUserDetailsForm,
  event,
}: Props): JSX.Element => {
  const { eventCompanyPlan } = useCompanyPlanSettings();
  const dispatch = useAppDispatch();
  const { track } = useContext(TRACKING_CONTEXT);
  const id = isHappeningNow
    ? "join-this-event-modal_primary-cta-continue"
    : "register-to-event-modal_primary-cta-continue";
  const mode = isHappeningNow
    ? CHECK_EMAIL_TO_LOGIN_MODES.JOIN_EVENT
    : CHECK_EMAIL_TO_LOGIN_MODES.EVENT_REGISTRATION;

  const { t } = useI18n(["common", "eventLanding", "auth"]);
  const styles = useStyles();
  const [loginMethodLoading, setLoginMethodLoading] = useState(false);
  const { addErrorNotification } = useNotificationActions();
  const { tableId, floorId } = useSelector(selectDirectTableLink);
  const { push, location } = useHistory();
  const showLoginWithPassword = useSelector(selectIsEmailPasswordEnabled);
  const showUserPreferredLoginBypass = useSelector(
    selectShowUserPreferredLoginBypass,
  );
  const preferredLoginMethods = useSelector(selectPreferredLoginMethods);
  const showPasswordLogin =
    showLoginWithPassword ||
    (showUserPreferredLoginBypass &&
      preferredLoginMethods.includes("password"));
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail({ ...email, value: e.target.value, message: "" });
  };

  const sendMagicLink = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      params.set(
        PROCEED_TO_EVENT_QUERY_PARAMETER,
        PROCEED_TO_EVENT_QUERY_PARAMETER_VALUE,
      );

      if (tableId && floorId) {
        params.set("table", tableId);
        params.set("floor", floorId);
      }

      await sendSignInLinkToEmail(
        email.value,
        `${location.pathname}?${params.toString()}`,
        isCustomDomainEnabled(eventCompanySettings, eventCompanyPlan)
          ? eventCompanySettings?.customDomain
          : undefined,
      );
      dispatch(setMagicLinkEmail(email.value));
      push(`/check-email-to-login?mode=${mode}`);
    } catch (error) {
      if (error instanceof Error) {
        addErrorNotification({
          message: t("auth:magic.link.invalid"),
          autoDismiss: 10,
        });
      }
    }
  }, [
    tableId,
    floorId,
    email.value,
    location.pathname,
    eventCompanySettings,
    eventCompanyPlan,
    dispatch,
    push,
    mode,
    addErrorNotification,
    t,
  ]);

  const proceedToEmailSignupOrSignin = async () => {
    track(Events.LOGIN_CLICKED_EMAIL_PASSWORD, {
      eventStatus: isHappeningNow ? "ongoing" : "upcoming",
    });

    try {
      setLoginMethodLoading(true);
      await emailSchema.validate(
        {
          email: email.value,
        },
        { context: { event } },
      );
      setGoToLogin(false);
      setShowUserDetailsForm(true);
    } catch (error) {
      setEmail({ ...email, message: t(getEmailErrorMessage(error)) });
    } finally {
      setLoginMethodLoading(false);
    }
  };

  const proceedToMagicLink = async (e: FormEvent) => {
    track(Events.LOGIN_CLICKED_MAGIC_LINK, {
      eventStatus: isHappeningNow ? "ongoing" : "upcoming",
    });

    e.preventDefault();
    e.stopPropagation();

    try {
      setLoginMethodLoading(true);
      await emailSchema.validate(
        {
          email: email.value,
        },
        { context: { event } },
      );

      await sendMagicLink();
    } catch (error) {
      setEmail({ ...email, message: t(getEmailErrorMessage(error)) });
    } finally {
      setLoginMethodLoading(false);
    }
  };

  const magicLinkText = useMemo(() => {
    if (loginMethodLoading) {
      return t("common:wait");
    }
    return t("auth:login.magic.link");
  }, [t, loginMethodLoading]);

  const isButtonDisabled = loginMethodLoading || email.value.length === 0;

  return (
    <form
      data-testid="email-submit-form"
      className={styles.form}
      onSubmit={proceedToMagicLink}
    >
      <FormTextInput
        size="medium"
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true, className: styles.emailLabel }}
        name="email"
        placeholder={t("eventLanding:email.address")}
        value={email.value}
        label={t("eventLanding:your.email")}
        type="email"
        onChange={handleOnChange}
        className={classnames(
          styles.formTextInput,
          !email.message && styles.mb20,
        )}
        inputClassName={styles.emailInput}
      />
      {email.message && (
        <FormHelperText className={styles.error} error>
          {email.message}
        </FormHelperText>
      )}

      <Container flex className={styles.loginButtons}>
        <Button
          id={id}
          fullWidth
          variant="secondary"
          type="submit"
          color="blue"
          disabled={isButtonDisabled}
          data-testid="magic-link-button"
          contentWrapperClassName={styles.buttonWrapper}
        >
          {magicLinkText}
        </Button>
        {showPasswordLogin && (
          <Button
            id={id}
            fullWidth
            variant="secondary"
            color="blue"
            contentWrapperClassName={styles.buttonWrapper}
            disabled={isButtonDisabled}
            data-testid="signin-button"
            onClick={() => proceedToEmailSignupOrSignin()}
          >
            {loginMethodLoading
              ? t("common:wait")
              : t("auth:login.email.password")}
          </Button>
        )}
      </Container>
    </form>
  );
};
